import { SetStateAction, useCallback, useEffect, useMemo, useState } from "react";
import {
  Link,
  MetaFunction,
  useFetcher,
  useLoaderData,
  useNavigate,
  useParams,
} from "@remix-run/react";
import {
  Accordion,
  AccordionItem,
  Card,
  Image,
  ScrollShadow,
  Select,
  Selection,
  SelectItem,
  Skeleton,
} from "@heroui/react";
import { MdKeyboardDoubleArrowRight } from "react-icons/md";
import { ProductCard } from "app/components/Product/ProductCard";
import { useAtom, useAtomValue } from "jotai";
import { fetchAllProductsApi } from "app/api/product/products.api";
import { removeKebabCase, setPageLimit, toKebabCase } from "app/utils/fn";
import TablePagination from "app/components/TablePagination";
import { ProductObject } from "app/api/product/product.type";
import { storageKey } from "app/utils/storageKeys";
import { Link as RemixLink } from "@remix-run/react";
import { BiFilter } from "react-icons/bi";
import { parse } from "cookie";
import { getSession } from "app/auth/sessions";
import { categoryList } from "app/atoms/category.atom";
import { category, categoryI } from "app/api/category/category.types";
import { UserType } from "app/api/user/user.types";
import { sortOrder } from "app/atoms/utils.atom";
import { sortOptions } from "app/utils/sortOptions";
import { categoryPageSchema } from "../_index_seo";
import { findAllCategory, findOneCategory } from "app/service/category.server";

import ReactMarkdown from "react-markdown";

export const meta: MetaFunction = (data) => {
  const category = toKebabCase(data?.params?.category || "");
  const subCategory = toKebabCase(data?.params?.subCategory || "");

  // Get footer content for enhanced SEO description (from loader data)
  const footerContent = (data?.data as any)?.footerContent;
  const firstParagraph = footerContent
    ? footerContent.split('\n\n').find((p: string) => p.trim().length > 50)?.trim()
    : null;

  const description = firstParagraph
    ? firstParagraph.substring(0, 160) + (firstParagraph.length > 160 ? "..." : "")
    : subCategory
      ? `Explore top-quality ${subCategory} with your logo. Perfect for giveaways, events, and corporate branding. Fast delivery Australia-wide.`
      : `Browse our range of custom ${category} designed for brand visibility. Ideal for promotions, staff, events, and more.`;

  return [
    {
      title: subCategory
        ? `Buy Custom ${subCategory} in Australia | Branded ${subCategory} for Promotions | Promotional Products Now`
        : `Shop Custom ${category} in Australia | Branded ${category} for Promotions | Promotional Products Now`,
    },

    {
      name: "description",
      content: description,
    },
    {
      tagName: "link",
      rel: "canonical",
      href: `https://www.promotionalproductsnow.au/category/${category}${subCategory ? `/${subCategory}` : ""}`,
    },
    {
      name: "og:description",
      content: description,
    },
    {
      property: "og:title",
      content: subCategory
        ? `Buy Custom ${subCategory} in Australia | Branded ${subCategory} for Promotions | Promotional Products Now`
        : `Shop Custom ${category} in Australia | Branded ${category} for Promotions | Promotional Products Now`,
    },
    {
      property: "og:url",
      content: `https://www.promotionalproductsnow.au/category/${category}${subCategory ? `/${subCategory}` : ""}`,
    },
    {
      property: "og:type",
      content: "website",
    },
  ];
};

export async function loader({
  params,
  request,
}: {
  params: { category: string; subCategory: string };
  request: Request;
}) {
  const session = await getSession(request.headers.get("Cookie"));
  const userType = session.get("userType");

  const url = new URL(request.url);
  const page = url.searchParams.get("page") || "1";

  // Parse cookies
  const cookies = parse(request.headers.get("Cookie") || "");

  // If the URL has a sort parameter, use it; otherwise, use the sort stored in cookies (current sort order) or default to "lowest-price"
  const sort = url.searchParams.get("sort") || cookies.sortOrder || "lowest-price";

  const limit = parseInt(url.searchParams.get("limit") || cookies.pageLimit || "16", 10);

  try {
    const categorySlug = toKebabCase(params.category || "");

    // Fetch categories for server-side validation
    const categories = (globalThis as any)?.categories || (await findAllCategory());

    const category: any = await findOneCategory(categorySlug, true);

    const footerContent = category?.footerContent ?? null;

    const { data } = await fetchAllProductsApi({
      category: removeKebabCase(params.category),
      subCategory: removeKebabCase(params.subCategory),
      page: parseInt(page, 10),
      limit,
      sort,
      userType: userType || UserType.Non_VIP,
      categories: categories || [], 
    });
    // Return sort along with the rest of the data
    return { ...data, sort, footerContent };
  } catch (e) {
    throw new Response(null, { status: 404, statusText: "Product Not Found" });
  }
}

const CategoryPage = () => {
  const storedCategoryList = useAtomValue(categoryList);
  // Use currentSortOrder as a string (atom)
  const [currentSortOrder, setCurrentSortOrder] = useAtom(sortOrder);

  const loaderData = useLoaderData<typeof loader>();
  const fetcher = useFetcher();
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(16);

  const { category, subCategory } = useParams();
  const [categories, setCategories] = useState<category[]>([]);

  // Get footer content from loader data
  const categoryFooterContent = loaderData.footerContent;

  const title = `Categories | Shop Custom ${
    subCategory ? subCategory : category
  } on Promotional Products Now `;
  const description = `Shop custom ${
    subCategory ? subCategory : category
  }, Perfect for events,giveaways and corporate branding`;

  // Update currentSortOrder state with loader value if necessary
  useEffect(() => {
    if (loaderData.sort && loaderData.sort !== currentSortOrder) {
      setCurrentSortOrder(loaderData.sort);
    }
  }, [loaderData.sort, currentSortOrder, setCurrentSortOrder]);

  const filteredProducts = useMemo(() => {
    if (!loaderData || !loaderData.docs) {
      return {
        totalPages: 0,
        hasPrevious: false,
        hasNext: false,
        nextPage: 0,
        prevPage: 0,
        limit: 0,
        totalItems: 0,
        products: [],
      };
    }

    const {
      docs: products,
      page,
      limit,
      totalPages,
      nextPage,
      prevPage,
      totalItems,
      hasNextPage,
      hasPrevPage,
      sort: mainSort,
    } = loaderData;

    return {
      page,
      totalPages,
      totalItems,
      hasPrevious: hasPrevPage,
      hasNext: hasNextPage,
      nextPage: nextPage || 0,
      prevPage: prevPage || 0,
      limit,
      products,
      sort: mainSort,
    };
  }, [loaderData]);

  const updateQueryParams = useCallback(
    ({ page, limit, sort }: { page: number; limit: number; sort?: string }) => {
      navigate(`?page=${page}&limit=${limit}&sort=${sort}`);
    },
    [navigate],
  );

  const handleNext = useCallback(
    (pageNumber: number) => {
      if (filteredProducts?.hasNext && !pageNumber) {
        updateQueryParams({
          page: filteredProducts.nextPage,
          limit,
          sort: currentSortOrder,
        });
        setCurrentPage(filteredProducts.nextPage);
      } else {
        updateQueryParams({
          page: pageNumber,
          limit,
          sort: currentSortOrder,
        });
        setCurrentPage(pageNumber);
      }
    },
    [
      filteredProducts.hasNext,
      filteredProducts.nextPage,
      limit,
      currentSortOrder,
      updateQueryParams,
    ],
  );

  const handlePrevious = useCallback(() => {
    if (filteredProducts.hasPrevious) {
      updateQueryParams({
        page: filteredProducts.prevPage,
        limit,
        sort: currentSortOrder,
      });
      setCurrentPage(filteredProducts.prevPage);
    }
  }, [
    filteredProducts.hasPrevious,
    filteredProducts.prevPage,
    limit,
    currentSortOrder,
    updateQueryParams,
  ]);

  const handleChangeLimit = useCallback(
    (newLimit: SetStateAction<number>) => {
      setLimit(newLimit);
      setPageLimit(newLimit as number);
      updateQueryParams({
        page: currentPage,
        limit: newLimit as number,
        sort: currentSortOrder,
      });
    },
    [currentPage, currentSortOrder, updateQueryParams],
  );

  const handleSortChange = useCallback(
    (keys: Selection) => {
      const newSort = Array.from(keys).join(",");
      setCurrentSortOrder(newSort);
      setCurrentPage(1);
      updateQueryParams({
        page: 1,
        limit,
        sort: newSort,
      });
    },
    [limit, updateQueryParams, setCurrentSortOrder],
  );

  useEffect(() => {
    const storedCategory = storedCategoryList;
    if (storedCategory) {
      const _categories = storedCategory;
      if (category) {
        const subCategories = _categories?.find(
          (v: any) => toKebabCase(v.name.toLowerCase()) === toKebabCase(category.toLowerCase()),
        );
        setCategories(subCategories?.subCategory || []);
      }
    }
  }, [category, subCategory, storedCategoryList]);

  useEffect(() => {
    if (localStorage) {
      const _limit = localStorage.getItem(storageKey.pageLimit) || "16";
      setLimit(Number(_limit));
    }
  }, []);

  return (
    <>
      <div className="space-y-6">
        <div className="w-full">
          <div className="flex flex-row py-3 px-5 md:px-0 md:pb-3 md:py-0">
            <div className="flex flex-row items-center w-full">
              <Link to="/">
                <span className="text-sm md:text-base text-primary">Home</span>
              </Link>
              <MdKeyboardDoubleArrowRight size={18} className=" text-gray dark:text-foreground" />
              {subCategory ? (
                <Link to={`/category/${toKebabCase(category || "")}`}>
                  <span className="text-sm md:text-base text-primary capitalize">
                    {removeKebabCase(category || "")}
                  </span>
                </Link>
              ) : (
                <span className="text-sm md:text-base  text-gray dark:text-foreground capitalize">
                  {removeKebabCase(category || "")}
                </span>
              )}
              {subCategory && (
                <>
                  {" "}
                  <MdKeyboardDoubleArrowRight
                    size={18}
                    className="text-gray  dark:text-foreground"
                  />
                  <span className="text-sm md:text-base text-gray dark:text-foreground capitalize">
                    {removeKebabCase(subCategory || "")}
                  </span>
                </>
              )}
            </div>
          </div>
        </div>
        <div className="space-y-4 md:space-y-8">
          <div className="px-5 md:px-0">
            <h1 className="flex items-center justify-start text-xl md:text-2xl lg:text-4xl xl:text-6xl font-bold capitalize">
              {subCategory ? removeKebabCase(subCategory || "") : removeKebabCase(category || "")}
            </h1>
            {filteredProducts && filteredProducts?.totalItems && (
              <div className=" italic text-lg text-foreground-800 mb-6">
                ({filteredProducts?.totalItems}) Products
              </div>
            )}
            <div className=" overflow-x-auto w-full my-2 md:hidden">
              <ScrollShadow className="w-full" orientation="horizontal" hideScrollBar={true}>
                <ul className="flex space-x-2">
                  {categories?.map((cat) => {
                    return (
                      <li
                        key={cat._id}
                        className={`px-3 py-1 rounded-md  w-fit transition-colors duration-200 ${
                          toKebabCase(cat.name)?.toLowerCase() ===
                          toKebabCase(subCategory || "")?.toLowerCase()
                            ? "bg-primary text-white"
                            : "bg-blue-100 text-blue-800 dark:bg-blue-700/30 dark:text-blue-200 border border-blue-400 dark:border-blue-500 border-dashed hover:bg-blue-200 dark:hover:bg-blue-600 dark:hover:text-white"
                        }`}
                      >
                        <RemixLink
                          to={`/category/${toKebabCase(category || "")}/${toKebabCase(cat.name)}`}
                          className="text-xs uppercase block w-max"
                        >
                          <span>{cat?.name}</span>
                        </RemixLink>
                      </li>
                    );
                  })}
                </ul>{" "}
              </ScrollShadow>
            </div>

            {category && categories ? (
              <div className="overflow-x-auto w-full my-2 hidden md:block">
                <ul className="flex flex-wrap gap-3 items-start">
                  {categories?.map((cat) => {
                    return (
                      <li
                        key={cat._id}
                        className={`px-4 py-2 rounded-lg transition-colors duration-200 w-fit
                      ${
                        toKebabCase(cat.name)?.toLowerCase() ===
                        toKebabCase(subCategory || "")?.toLowerCase()
                          ? "bg-primary text-white"
                          : "bg-blue-100 text-blue-800 dark:bg-blue-700/30 dark:text-blue-200 border border-blue-400 dark:border-blue-500 border-dashed hover:bg-blue-200 dark:hover:bg-blue-600 dark:hover:text-white"
                      }`}
                      >
                        <RemixLink
                          to={`/category/${toKebabCase(category || "")}/${toKebabCase(cat.name)}`}
                          className="uppercase text-sm font-medium tracking-wide block"
                        >
                          {cat?.name}
                        </RemixLink>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ) : null}
          </div>

          <Accordion className="bg-zinc-50 dark:bg-zinc-800 rounded-xl">
            <AccordionItem
              key="1"
              aria-label="Sort"
              subtitle="Press to expand"
              title={
                <div className="flex space-x-3 items-center">
                  <BiFilter size={24} className="text-primary" />
                  <span className=" text-foreground md:text-2xl font-semibold text-center">
                    Sort
                  </span>
                </div>
              }
            >
              <div className="justify-end w-full md:w-2/5">
                <div className="flex items-end  justify-between space-x-3 mb-2 w-full">
                  <Select
                    variant="bordered"
                    label="Sort by: Price"
                    labelPlacement="inside"
                    className="max-w-xs text-center"
                    placeholder=""
                    classNames={{
                      trigger: ["border-zinc-100 rounded-md py-1"],
                      label: ["text-sm w-28"],
                      popoverContent: "w-64",
                    }}
                    items={sortOptions}
                    size="sm"
                    selectedKeys={[currentSortOrder]}
                    onSelectionChange={handleSortChange}
                  >
                    {(option) => <SelectItem key={option.key}>{option.label}</SelectItem>}
                  </Select>
                </div>
              </div>
            </AccordionItem>
          </Accordion>

          {fetcher.state === "loading" ? (
            <div className="flex flex-col gap-10 md:px-20 px-5 w-full">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {[...Array(8)].map((_, index) => (
                  <Card key={index} className="w-full space-y-5 p-4" radius="lg">
                    <Skeleton className="rounded-lg">
                      <div className="h-24 rounded-lg bg-default-300" />
                    </Skeleton>
                    <div className="space-y-3">
                      <Skeleton className="w-3/5 rounded-lg">
                        <div className="h-3 w-3/5 rounded-lg bg-default-200" />
                      </Skeleton>
                      <Skeleton className="w-4/5 rounded-lg">
                        <div className="h-3 w-4/5 rounded-lg bg-default-200" />
                      </Skeleton>
                      <Skeleton className="w-2/5 rounded-lg">
                        <div className="h-3 w-2/5 rounded-lg bg-default-300" />
                      </Skeleton>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          ) : filteredProducts.products.length === 0 ? (
            <div className="flex flex-col items-center justify-center w-full py-20 text-center text-gray-500">
              <Image src="/empty_product.png" className="w-32 h-32 mt-2 mb-6 min-w-60 min-h-60" />

              <p className="text-lg font-semibold">No products found</p>
              <p className="text-sm text-gray-400 mt-2">
                Try adjusting your browsing another category
              </p>
            </div>
          ) : (
            <div className="flex flex-col gap-10 md:px-20 px-5 w-full">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {filteredProducts.products.map((item: ProductObject) => (
                  <ProductCard
                    key={item?._id}
                    isSpecialSupplier={item?.supplier?.isSpecialSupplier ?? false}
                    labels={item?.labels || []}
                    image={item?.overview?.heroImage}
                    images={item?.product?.images}
                    title={item?.overview?.name}
                    productCode={item?.overview?.code}
                    description={item?.product?.description}
                    userTypePrice={item?.userTypePrice}
                    qty={item?.quantity}
                    slug={item?.slug}
                    category={
                      item?.category?.name ||
                      item?.globalProductCategory?.name ||
                      item?.product?.categorisation?.productType?.typeName
                    }
                    subCatgory={
                      item?.subCategory?.name ||
                      item?.globalProductSubCategory?.name ||
                      item?.product?.categorisation?.supplierSubCategory
                    }
                  />
                ))}
              </div>

              <div className="flex w-full justify-center px-5 pb-1">
                <TablePagination
                  totalPages={filteredProducts.totalPages}
                  currentPage={filteredProducts.page}
                  handlePrevious={handlePrevious}
                  handleNext={(p) => handleNext(p as number)}
                  setLimit={handleChangeLimit}
                />
              </div>
            </div>
          )}
        </div>
        {/* Category Footer Content */}
        {categoryFooterContent && (
          <div className="px-5 md:px-20 py-8 md:py-12 lg:rounded-xl bg-zinc-50 dark:bg-zinc-900/50">
            <div className="max-w-4xl mx-auto">
              <div className="text-base md:text-lg prose prose-zinc dark:prose-invert max-w-none">
                <ReactMarkdown
                  components={{
                    h1: ({ children }) => (
                      <h1 className="text-3xl md:text-4xl font-bold mt-8 mb-4 text-foreground-900 dark:text-foreground-100">
                        {children}
                      </h1>
                    ),
                    h2: ({ children }) => (
                      <h2 className="text-2xl md:text-3xl font-bold mt-8 mb-4 text-foreground-900 dark:text-foreground-100">
                        {children}
                      </h2>
                    ),
                    h3: ({ children }) => (
                      <h3 className="text-xl md:text-2xl font-bold mt-8 mb-4 text-foreground-900 dark:text-foreground-100">
                        {children}
                      </h3>
                    ),
                    p: ({ children }) => (
                      <p className="mb-4 text-foreground-700 dark:text-foreground-300 leading-relaxed">
                        {children}
                      </p>
                    ),
                    ul: ({ children }) => (
                      <ul className="list-disc list-inside mb-4 text-foreground-700 dark:text-foreground-300 space-y-2">
                        {children}
                      </ul>
                    ),
                    ol: ({ children }) => (
                      <ol className="list-decimal list-inside mb-4 text-foreground-700 dark:text-foreground-300 space-y-2">
                        {children}
                      </ol>
                    ),
                    li: ({ children }) => (
                      <li className="mb-2">{children}</li>
                    ),
                    strong: ({ children }) => (
                      <strong className="font-semibold text-foreground-900 dark:text-foreground-100">
                        {children}
                      </strong>
                    ),
                    em: ({ children }) => (
                      <em className="italic">{children}</em>
                    ),
                    a: ({ href, children }) => (
                      <a
                        href={href}
                        className="text-primary hover:underline"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {children}
                      </a>
                    ),
                  }}
                >
                  {categoryFooterContent}
                </ReactMarkdown>
              </div>
            </div>
          </div>
        )}
      </div>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(
            categoryPageSchema({
              category: {
                title: title,
                description: description,
                slug: `${category}${subCategory ? `/${subCategory}` : ""}`,
              },
              subcategories: categories.map((cat) => ({
                title: cat.name,
                slug: cat.name,
              })),
              products: filteredProducts.products,
              footerContent: categoryFooterContent ?? "",
            }),
          ),
        }}
      />
    </>
  );
};

export default CategoryPage;
